import React, { useEffect, useState, useMemo } from 'react';
import { neonQuery } from '../services/api';

// Daily Detail modal — drill-in for a row on the VIP Cash report's
// Daily breakdown. Lists every vip_cash_ledger entry posted on the
// selected ET calendar day, with a source-mix summary at the top and a
// filter chip per source. Each row → click the customer to open the
// per-customer ledger modal.

const fmt$ = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtSigned = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) < 0.005) return '$0.00';
  return (v > 0 ? '+' : '−') + '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' });
};
// Parse YYYY-MM-DD as a calendar date — the DB returns ET-bucketed days
// as DATE which serializes to UTC midnight; new Date() then shifts to the
// user's local zone and the day-before is shown.
const WEEKDAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const calendarDate = (iso) => {
  if (!iso) return null;
  const m = String(iso).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { y: parseInt(m[1], 10), mo: parseInt(m[2], 10), d: parseInt(m[3], 10), isoDate: `${m[1]}-${m[2]}-${m[3]}` };
};
const fmtDay = (iso) => {
  const c = calendarDate(iso);
  if (!c) return '';
  // Compute weekday from a UTC date so it's not shifted by local timezone
  const dt = new Date(Date.UTC(c.y, c.mo - 1, c.d));
  return `${WEEKDAYS[dt.getUTCDay()]}, ${MONTHS[c.mo - 1]} ${c.d}, ${c.y}`;
};

const SOURCE_META = {
  mongo_earn:               { label: 'Mongo earn',     bg: '#e8f5e9', fg: '#2e7d32', border: '#a5d6a7' },
  mongo_redeem:             { label: 'Mongo redeem',   bg: '#fff3e0', fg: '#e65100', border: '#ffb74d' },
  mongo_expire:             { label: 'Mongo expire',   bg: '#f5f5f5', fg: '#616161', border: '#bdbdbd' },
  mongo_cancellation:       { label: 'Mongo cancel',   bg: '#f5f5f5', fg: '#616161', border: '#bdbdbd' },
  bc_order:                 { label: 'BC order',       bg: '#e3f2fd', fg: '#0d47a1', border: '#90caf9' },
  inferred_opening_balance: { label: 'Opening bal.',   bg: '#f3e5f5', fg: '#6a1b9a', border: '#ce93d8' },
  inferred_external_credit: { label: 'External credit',bg: '#f3e5f5', fg: '#6a1b9a', border: '#ce93d8' },
  inferred_external_debit:  { label: 'External debit', bg: '#f3e5f5', fg: '#6a1b9a', border: '#ce93d8' },
  bc_manual_inferred:       { label: 'Manual BC',      bg: '#f3e5f5', fg: '#6a1b9a', border: '#ce93d8' },
};
const DEFAULT_META = { label: '?', bg: '#fafafa', fg: '#757575', border: '#e0e0e0' };

function SourceBadge({ source }) {
  const m = SOURCE_META[source] || DEFAULT_META;
  return (
    <span style={{
      display: 'inline-block', fontSize: 9, fontWeight: 600, lineHeight: 1.4,
      padding: '1px 6px', borderRadius: 3, whiteSpace: 'nowrap',
      background: m.bg, color: m.fg, border: `1px solid ${m.border}`,
    }}>{m.label}</span>
  );
}

function describe(row) {
  const c = String(row.category || '').toLowerCase();
  if (row.source === 'inferred_opening_balance') return 'Opening balance';
  if (row.source === 'inferred_external_credit') return 'External credit (manual BC)';
  if (row.source === 'inferred_external_debit')  return 'External debit (manual BC)';
  if (row.source === 'bc_manual_inferred')       return 'Manual BC adjustment';
  if (c === 'welcome')      return 'Welcome reward';
  if (c === 'monthly')      return 'Monthly reward';
  // The connector writes category 'order' for a status-ship earn; the Mongo era wrote
  // 'order_reward'. Same thing - accept both or every SQL-era earn loses its order number.
  if (c === 'order_reward' || c === 'order') return row.order_id ? `Order reward #${row.order_id}` : 'Order reward';
  if (c === 'goodwill')     return 'Goodwill credit';
  if (c === 'manual_adjustment') return row.direction === 'credit' ? 'Manual credit' : 'Manual debit';
  if (c === 'redeem')       return row.order_id ? `Redemption on #${row.order_id}` : 'Redemption';
  if (c === 'expire')       return 'Expired';
  if (c === 'cancellation') {
    const d = String(row.description || '').toLowerCase();
    if (d.includes('monthly reward reversal')) return 'Monthly reward reversal';
    if (d.includes('phantom-order') || d.includes('phantom order')) return 'Phantom-order cancellation';
    if (d.includes('vip cancellation') || d.includes('vip membership')) return 'VIP membership cancellation';
    return row.description ? `Cancellation — ${row.description.slice(0, 50)}` : 'Cancellation';
  }
  if (c === 'audit_reconciliation') return 'Audit reconciliation';
  return row.description || c || row.direction;
}

export default function DailyDetailModal({ open, onClose, day, onOpenCustomer }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sourceFilter, setSourceFilter] = useState('');
  const [includeMonthly, setIncludeMonthly] = useState(false);

  useEffect(() => {
    if (!open || !day) return;
    let cancelled = false;
    setLoading(true); setError(null); setRows([]); setSourceFilter('');
    const c = calendarDate(day);
    const isoDate = c ? c.isoDate : String(day);
    neonQuery('vip_cash_ledger_by_date', { date: isoDate, includeMonthly })
      .then((r) => { if (!cancelled) setRows(Array.isArray(r) ? r : []); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, day, includeMonthly]);

  // Group by source for the summary strip + filter chips.
  const sourceGroups = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const k = r.source || '?';
      if (!map.has(k)) map.set(k, { source: k, count: 0, credit: 0, debit: 0 });
      const g = map.get(k);
      g.count += 1;
      const amt = Number(r.amount || 0);
      if (r.direction === 'credit') g.credit += amt; else g.debit += amt;
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [rows]);

  const totals = useMemo(() => {
    let credit = 0, debit = 0;
    for (const r of rows) {
      if (!r.included_in_balance) continue;
      const amt = Number(r.amount || 0);
      if (r.direction === 'credit') credit += amt; else debit += amt;
    }
    return { credit, debit, net: credit - debit };
  }, [rows]);

  const visible = useMemo(() => {
    if (!sourceFilter) return rows;
    return rows.filter(r => r.source === sourceFilter);
  }, [rows, sourceFilter]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(10, 20, 40, 0.55)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(1040px, 100%)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', margin: 0 }}
      >
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span>Ledger entries · {fmtDay(day)}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--gray-700)', fontWeight: 400, cursor: 'pointer' }}>
              <input type="checkbox" checked={includeMonthly} onChange={(e) => setIncludeMonthly(e.target.checked)} />
              Include monthly grants &amp; reversals
            </label>
            <a onClick={onClose} style={{ cursor: 'pointer', color: 'var(--gray-500)', fontSize: 18, fontWeight: 700, lineHeight: 1 }}>×</a>
          </span>
        </div>

        {/* Totals strip */}
        <div style={{ padding: '8px 16px', background: 'var(--gray-50)', fontSize: 11, color: 'var(--gray-700)', display: 'flex', gap: 18, borderBottom: '1px solid var(--gray-100)', flexWrap: 'wrap' }}>
          <span>Credits: <strong style={{ color: 'var(--green, #2e7d32)' }}>{fmt$(totals.credit)}</strong></span>
          <span>Debits: <strong style={{ color: 'var(--orange)' }}>{fmt$(totals.debit)}</strong></span>
          <span>Net: <strong style={{ color: totals.net >= 0 ? 'var(--navy)' : 'var(--red, #c0392b)' }}>{fmtSigned(totals.net)}</strong></span>
          <span style={{ marginLeft: 'auto', color: 'var(--gray-500)' }}>{rows.length} entries</span>
        </div>

        {/* Source-mix card grid */}
        {sourceGroups.length > 0 && (
          <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--gray-100)', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <SourceChip
              active={sourceFilter === ''}
              onClick={() => setSourceFilter('')}
              label="All sources"
              count={rows.length}
              credit={totals.credit}
              debit={totals.debit}
            />
            {sourceGroups.map(g => (
              <SourceChip
                key={g.source}
                active={sourceFilter === g.source}
                onClick={() => setSourceFilter(sourceFilter === g.source ? '' : g.source)}
                source={g.source}
                count={g.count}
                credit={g.credit}
                debit={g.debit}
              />
            ))}
          </div>
        )}

        <div style={{ overflow: 'auto', flex: 1 }}>
          {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)', textAlign: 'center' }}>Loading…</div>}
          {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red, #c0392b)' }}>Failed to load: {error}</div>}
          {!loading && !error && rows.length === 0 && (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)', textAlign: 'center' }}>No ledger entries on this date.</div>
          )}
          {!loading && !error && visible.length > 0 && (
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)', position: 'sticky', top: 0 }}>
                  <th style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 700, color: 'var(--gray-500)', width: 70 }}>Time</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Description</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Source</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row, i) => {
                  const excluded = !row.included_in_balance;
                  const signed = (row.direction === 'credit' ? 1 : -1) * Number(row.amount || 0);
                  const color = excluded ? 'var(--gray-400, #9e9e9e)'
                    : row.direction === 'credit' ? 'var(--green, #2e7d32)'
                    : row.category === 'redeem' ? 'var(--orange)'
                    : 'var(--gray-500)';
                  return (
                    <tr key={row.id ?? i} style={{
                      borderBottom: '1px solid var(--gray-100)',
                      background: i % 2 === 0 ? '#fff' : 'var(--gray-50)',
                      opacity: excluded ? 0.55 : 1,
                      textDecoration: excluded ? 'line-through' : 'none',
                    }}>
                      <td style={{ padding: '7px 10px', color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>{fmtTime(row.txn_date)}</td>
                      <td
                        style={{ padding: '7px 8px', cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); onOpenCustomer && onOpenCustomer({
                          bcCustomerId: row.bc_customer_id,
                          customerName: row.name,
                          customerEmail: row.email,
                        }); }}
                      >
                        <span style={{ color: 'var(--navy)', fontWeight: 600, borderBottom: '1px dashed rgba(0,59,92,0.35)' }}>
                          {row.name || '—'}
                        </span>
                        <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 1 }}>
                          {row.email || `BC #${row.bc_customer_id}`}
                        </div>
                      </td>
                      <td style={{ padding: '7px 8px', color: 'var(--navy)' }}>{describe(row)}</td>
                      <td style={{ padding: '7px 8px' }}><SourceBadge source={row.source} /></td>
                      <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 600, color }}>{fmtSigned(signed)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Source filter chip ──────────────────────────────────────────────────────

function SourceChip({ active, onClick, source, label, count, credit, debit }) {
  const m = source ? (SOURCE_META[source] || DEFAULT_META) : { label: 'All sources', bg: '#fff', fg: 'var(--navy)', border: 'var(--gray-200)' };
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '6px 10px', borderRadius: 4, cursor: 'pointer', textAlign: 'left',
        background: active ? m.fg : m.bg,
        color: active ? '#fff' : m.fg,
        border: `1px solid ${active ? m.fg : m.border}`,
        fontSize: 10, fontWeight: 600, lineHeight: 1.4,
        minWidth: 110,
      }}
    >
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label || m.label}</div>
      <div style={{ fontSize: 11, fontWeight: 700, marginTop: 2 }}>{count} {count === 1 ? 'entry' : 'entries'}</div>
      <div style={{ fontSize: 10, opacity: 0.85, marginTop: 1 }}>
        {credit > 0 && <span>+{fmt$(credit)}</span>}
        {credit > 0 && debit > 0 && <span> · </span>}
        {debit > 0 && <span>−{fmt$(debit)}</span>}
        {credit === 0 && debit === 0 && <span>—</span>}
      </div>
    </button>
  );
}
